import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const ProductFAQ = () => {
  const [openItems, setOpenItems] = useState({});

  const faqs = [
    {
      id: 'models',
      question: 'Q: Which AI models do I get access to?',
      answer: 'A: All of them! GPT-4o, o3, GPT-4.1, GPT-4.5, GPT-4.1 mini, plus automatic access to all future model releases. Same access as official $25/month subscribers.'
    },
    {
      id: 'future',
      question: 'Q: Will I get new models like o3 and future releases?',
      answer: 'A: Yes, automatically. You get the same model access as official ChatGPT Plus subscribers, including all current and future AI models OpenAI releases.'
    },
    {
      id: 'quality',
      question: 'Q: Is the AI quality the same as official ChatGPT Plus?',
      answer: 'A: Identical quality. You access the exact same models (GPT-4o, o3, etc.) with the same capabilities, just at 65% less cost.'
    },
    {
      id: 'reliability',
      question: 'Q: How does shared access work? Is it reliable?',
      answer: 'A: Our professionally managed system maintains 99.9% uptime with intelligent load balancing. You get consistent, reliable access to all premium features.'
    },
    {
      id: 'usability',
      question: 'Q: What if I\'m not tech-savvy? Will I be able to use this effectively?',
      answer: 'A: Absolutely! The interface is identical to official ChatGPT Plus. If you can use ChatGPT, you can use our service. Plus, we provide comprehensive support and tutorials.'
    },
    {
      id: 'results',
      question: 'Q: How quickly will I see results?',
      answer: 'A: Immediately. Access is activated within 5 minutes of purchase. Most users see productivity improvements from day one.'
    },
    {
      id: 'privacy',
      question: 'Q: Is my data safe and private?',
      answer: 'A: Yes. We use bank-level 256-bit SSL encryption and complete data isolation. Your conversations are private and secure, with no cross-contamination between users.'
    },
    {
      id: 'support',
      question: 'Q: What support do you provide?',
      answer: 'A: Comprehensive support including 2-hour response time, email/chat assistance, setup tutorials, and an active community of 5,000+ users.'
    },
    {
      id: 'cancellation',
      question: 'Q: Can I cancel anytime?',
      answer: 'A: Yes, complete flexibility. Cancel anytime with no penalties or long-term commitments.'
    },
    {
      id: 'issues',
      question: 'Q: What happens if there are technical issues?',
      answer: 'A: Our 24/7 monitoring system ensures 99.9% uptime. In the rare case of issues, we provide immediate support and account replacement if needed.'
    }
  ];

  const toggleItem = (id) => {
    setOpenItems(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Get answers to common questions about our ChatGPT Plus service
          </p>
        </div>

        {/* FAQ Items */}
        <div className="max-w-4xl mx-auto">
          {faqs.map((faq) => (
            <div 
              key={faq.id}
              className="mb-4 bg-white rounded-2xl border border-gray-200 overflow-hidden hover:border-black transition-colors duration-200"
            >
              <button
                onClick={() => toggleItem(faq.id)}
                className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
              >
                <h3 className="text-lg font-semibold text-gray-900 pr-4">
                  {faq.question}
                </h3>
                <span className="text-gray-500 flex-shrink-0">
                  {openItems[faq.id] ? (
                    <ChevronUp className="w-5 h-5" />
                  ) : (
                    <ChevronDown className="w-5 h-5" />
                  )}
                </span>
              </button>
              
              {openItems[faq.id] && (
                <div className="px-8 pb-6 border-t border-gray-100">
                  <p className="text-gray-700 leading-relaxed pt-4">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Contact Information */}
        <div className="text-center mt-16 p-8 bg-gray-50 rounded-2xl max-w-2xl mx-auto">
          <h3 className="text-xl font-semibold text-gray-900 mb-4">
            Still Have Questions?
          </h3>
          <p className="text-gray-600 mb-6">
            Our support team is here to help you make the right decision
          </p>
          <div className="flex flex-col md:flex-row gap-4 justify-center">
            <a 
              href="mailto:support@chatgptplus.com"
              className="inline-flex items-center px-6 py-3 bg-black text-white rounded-2xl hover:bg-gray-800 transition-colors duration-200"
            >
              📧 Email Support
            </a>
            <a 
              href="#chat"
              className="inline-flex items-center px-6 py-3 border-2 border-black text-black rounded-2xl hover:bg-black hover:text-white transition-colors duration-200"
            >
              💬 Live Chat
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductFAQ;